import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Phone, BadgeCheck, Building2, FileText, Users, AlertTriangle, Download } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { FAQSection } from "@/components/FAQSection";
import { CTABand } from "@/components/CTABand";
import { SITE, IMAGES, CAPABILITIES_PDF } from "@/content/site";

const WHY_POINTS = [
  {
    icon: Users,
    title: "Bather loads residential pools never see",
    text: "A community pool can host more swimmers in one weekend than a backyard pool sees in a season. Chemistry that's 'close enough' at home fails fast under commercial demand.",
  },
  {
    icon: AlertTriangle,
    title: "Downtime has a price tag",
    text: "A closed pool means resident complaints, lost bookings, and liability exposure. Routine equipment care and early problem detection keep your amenity open.",
  },
  {
    icon: FileText,
    title: "Records are part of the job",
    text: "Boards, ownership groups, and insurers expect documentation. Dated chemistry readings and service records come standard with our commercial visits.",
  },
  {
    icon: BadgeCheck,
    title: "Standards you can point to",
    text: "CPO certification is the recognized credential for commercial pool operation — it's how we manage every reading, adjustment, and inspection on your property.",
  },
];

const STANDARDS = [
  { num: "01", title: "CPO certified oversight", text: "Every visit, reading, and adjustment is performed to Certified Pool Operator standards — the benchmark for commercial pool operation." },
  { num: "02", title: "Code-conscious chemistry", text: "Chemistry managed with Florida's public pool requirements in mind: tighter targets, more frequent checks, no guesswork under heavy bather loads." },
  { num: "03", title: "Documentation & reporting", text: "Dated readings and service logs your HOA board, ownership group, or property manager can actually use." },
  { num: "04", title: "Preventive equipment care", text: "Filters, pumps, heaters, and feed systems checked on a schedule — small issues caught before they close the pool." },
];

const VISIT_INCLUDES = [
  "Professional water testing & chemical balancing",
  "Skimming, brushing & vacuuming as needed",
  "Filter monitoring and scheduled filter cleaning",
  "Pump, heater & feed system checks",
  "Safety and circulation equipment visual inspection",
  "Dated service records after every visit",
];

const PROPERTY_TYPES = [
  { name: "Condominiums & HOAs", note: "Amenity pools with boards that want records, responsiveness, and residents off their backs." },
  { name: "Apartment Communities", note: "High-traffic pools that need chemistry managed around constant daily use." },
  { name: "Hotels & Inns", note: "Guest-facing pools where water quality shows up directly in reviews and rebookings." },
  { name: "Community & Amenity Pools", note: "Shared facilities that need dependable scheduling and clear communication with managers." },
];

const COMMERCIAL_FAQS = [
  {
    q: "Do you service commercial pools?",
    a: "Yes. We provide commercial pool maintenance for condominiums, HOAs, apartment communities, and hospitality properties across Pinellas County. As a CPO certified company experienced in commercial property pool maintenance, we manage chemistry, documentation, and equipment care to commercial standards.",
  },
  {
    q: "How often should a commercial pool be serviced?",
    a: "More often than a residential pool. Commercial bather loads and Florida's heat mean chemistry can shift daily. We'll recommend a visit schedule based on your pool's size, usage, and season — and document every visit.",
  },
  {
    q: "Can you provide records for our board or ownership group?",
    a: "Yes. Dated water chemistry readings and service records are part of every commercial visit, formatted so boards, managers, and ownership groups can review exactly how the pool is being maintained.",
  },
  {
    q: "Do you work directly with property managers?",
    a: "Yes — we already partner with multiple property management companies in Pinellas County. Managers get a direct line to a local pro, prompt photo alerts when something needs attention, and clear communication before small issues become resident complaints.",
  },
];

export default function CommercialPage() {
  return (
    <>
      <SEO
        title="Commercial Pool Service & Maintenance in Pinellas County FL | Point of Pines"
        description="CPO certified commercial pool maintenance for HOAs, condominiums, apartments and hotels across Pinellas County FL. Documented chemistry readings, preventive equipment care, reliable scheduling. Call (727) 744-5908."
        path="/commercial-pool-service/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Commercial Pool Maintenance",
          provider: {
            "@type": "HomeAndConstructionBusiness",
            name: SITE.name,
            telephone: "+1-727-744-5908",
            url: SITE.domain,
          },
          areaServed: { "@type": "AdministrativeArea", name: "Pinellas County, FL" },
        }}
      />
      <PageHero
        overline="Commercial Pool Maintenance — Pinellas County, FL"
        title="Commercial Pool Service"
        accent="Held to a Higher Standard"
        description="CPO certified pool maintenance for HOAs, condominiums, apartment communities, and hospitality properties — with the documentation and reliability shared pools demand."
        image={IMAGES.communityPool}
        imageAlt="Condominium community pool and spa in Pinellas County Florida maintained by CPO certified professionals"
      />

      <section data-testid="commercial-why-section" className="py-16 sm:py-24 lg:py-32 bg-shell">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-pool font-semibold">Why Commercial Is Different</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
              A shared pool is a liability — or an asset
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Commercial pool care is a different discipline than residential service. The chemistry
              tolerances are tighter, the documentation matters more, and a bad week is measured in
              complaints and closures, not just cloudy water.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
            {WHY_POINTS.map((p, i) => (
              <motion.div
                key={p.title}
                data-testid={`commercial-why-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
                className="bg-white rounded-2xl border border-slate-200/80 p-7 sm:p-8 transition-shadow duration-300 hover:shadow-xl hover:shadow-cyan-500/10"
              >
                <span className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center">
                  <p.icon className="w-6 h-6 text-sky2" />
                </span>
                <h3 className="mt-5 font-display text-xl font-semibold text-navy tracking-tight leading-snug">{p.title}</h3>
                <p className="mt-3 text-sm sm:text-[15px] text-slate-500 leading-relaxed">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="commercial-standards-section" className="py-16 sm:py-24 bg-navy text-white relative overflow-hidden">
        <div className="noise-overlay absolute inset-0 opacity-[0.04] pointer-events-none" />
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto relative">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-sky2 font-semibold">Our Standard of Care</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15]">
              How your pool gets run, every visit
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-white/15">
            {STANDARDS.map((s, i) => (
              <motion.div
                key={s.num}
                data-testid={`commercial-page-standard-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="group py-8 sm:py-10 sm:px-7 border-b sm:border-b-0 sm:border-l border-white/15 first:border-l-0"
              >
                <span className="font-mono text-sm text-sky2 transition-colors duration-300 group-hover:text-aqua">{s.num}</span>
                <h3 className="mt-3 font-display text-lg font-semibold tracking-tight leading-snug">{s.title}</h3>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-14 grid lg:grid-cols-2 gap-10 items-start">
            <Reveal>
              <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">Every commercial visit includes</h3>
              <ul className="mt-6 space-y-3.5">
                {VISIT_INCLUDES.map((item, i) => (
                  <li key={item} data-testid={`commercial-included-${i}`} className="flex items-start gap-3.5">
                    <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-aqua" />
                    </span>
                    <span className="text-sm sm:text-[15px] text-slate-300 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="bg-white/[0.06] backdrop-blur-sm ring-1 ring-white/15 rounded-2xl p-7 sm:p-8">
                <span className="w-12 h-12 rounded-xl bg-aqua/15 flex items-center justify-center">
                  <BadgeCheck className="w-6 h-6 text-aqua" />
                </span>
                <p className="mt-4 font-display font-semibold tracking-tight leading-snug text-lg">
                  CPO certified, and experienced in commercial property pool maintenance.
                </p>
                <p className="mt-4 text-sm text-slate-300 leading-relaxed">
                  Your board, your manager, and your insurer all want the same thing: proof the pool
                  is being run professionally. That&apos;s exactly what our visits produce.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="commercial-properties-section" className="py-16 sm:py-24 lg:py-32 bg-shell">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-pool font-semibold">Who We Serve</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
              Commercial properties across Pinellas County
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
            {PROPERTY_TYPES.map((t, i) => (
              <motion.div
                key={t.name}
                data-testid={`commercial-property-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (i % 2) * 0.08 }}
                className="flex items-start gap-4 bg-white rounded-2xl border border-slate-200/80 p-6 sm:p-7 transition-shadow duration-300 hover:shadow-xl hover:shadow-cyan-500/10"
              >
                <span className="w-11 h-11 rounded-xl bg-mist flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5 text-pool" />
                </span>
                <div>
                  <h3 className="font-display text-lg font-semibold text-navy tracking-tight">{t.name}</h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">{t.note}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <FAQSection
        faqs={COMMERCIAL_FAQS}
        overline="Commercial FAQ"
        title="Questions boards & managers ask"
      />

      <section data-testid="commercial-page-cta" className="py-16 sm:py-20 bg-mist">
        <div className="px-4 sm:px-6 lg:px-12 max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
              Get a commercial maintenance proposal
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Tell us about your property — pool size, usage, and who we&apos;ll be working with.
              We&apos;ll walk the pool, assess the equipment, and put together a clear maintenance
              plan with the documentation your stakeholders expect.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3.5">
              <Link
                to="/contact/"
                data-testid="commercial-page-quote-button"
                className="group inline-flex items-center justify-center gap-2.5 bg-navy text-white font-bold px-8 py-4 rounded-full transition-all duration-300 hover:bg-pool hover:text-white hover:shadow-lg hover:shadow-cyan-500/25"
              >
                Request a Commercial Quote
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={SITE.phoneHref}
                data-testid="commercial-page-call-button"
                className="inline-flex items-center justify-center gap-2.5 border-2 border-navy text-navy font-bold px-8 py-4 rounded-full transition-all duration-300 hover:bg-navy hover:text-white"
              >
                <Phone className="w-5 h-5" /> {SITE.phone}
              </a>
            </div>
            <a
              href={CAPABILITIES_PDF}
              download
              data-testid="commercial-download-pdf-button"
              className="group mt-5 inline-flex items-center gap-2.5 font-display font-bold text-pool hover:text-ocean transition-colors"
            >
              <Download className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              Download our one-page capabilities sheet (PDF) — built to bring to a board vote
            </a>
          </Reveal>
        </div>
      </section>

      <CTABand
        title="Your residents notice the pool first"
        text="Put your commercial pool on a CPO certified maintenance program and make it the amenity that sells your property."
      />
    </>
  );
}
